import { useMemo, useState } from 'react';
import { I18nProvider, useI18n } from '@react/i18n';
import { createVratkyApi } from './api/vratkyApi';
import { ChoiceBoxes } from './ChoiceBoxes';
import { BaseInput } from './steps/BaseInput';
import { ProductSelector } from './steps/ProductSelector';
import { VratkaForm } from './steps/VratkaForm';
import { ReklamaceForm } from './steps/ReklamaceForm';
import { KontaktniUdaje } from './steps/KontaktniUdaje';
import { Summary } from './steps/Summary';
import { Finished } from './steps/Finished';
import { LookupData, Page, TypVraceni, UserInput, VratkyAppProps } from './types';
import './vratky.css';

/**
 * Zákaznické vratky a reklamace – přepis standalone react-vratky (bola-tools)
 * do islandu nad proxy /api/v1/vratky. Stavový automat stránek:
 * init → baseInput → product_selector → form → contact → summary → finished.
 */

export type UpdateUserInput = <K extends keyof UserInput>(name: K, value: UserInput[K]) => void;

const INITIAL_INPUT: UserInput = {
    duvod_vraceni: '14_dni',
    duvod_vraceni_detail: '',
    cislo_uctu_user: '',
    iban_user: '',
    vracene_polozky: [],
    user_cislo_dokladu: '',
    user_email: '',
    no_order_email: false,
    no_account_number: false,
    contact_phone: '',
    contact_name: '',
    contact_email: '',
    has_special_conditions: false,
    special_conditions: '',
};

function App({ apiBaseUrl, language, emailHints = false }: VratkyAppProps) {
    const t = useI18n();
    const api = useMemo(() => createVratkyApi(apiBaseUrl), [apiBaseUrl]);
    const [typVraceni, setTypVraceni] = useState<TypVraceni>('vratka');
    const [page, setPage] = useState<Page>('init');
    const [data, setData] = useState<LookupData | null>(null);
    const [stitekInfo, setStitekInfo] = useState('');
    const [userInput, setUserInput] = useState<UserInput>(INITIAL_INPUT);

    const update: UpdateUserInput = (name, value) => {
        setUserInput((prev) => {
            const next = { ...prev, [name]: value };
            if (name === 'no_account_number' && value === true) {
                next.cislo_uctu_user = '';
                next.iban_user = '';
            }
            if (name === 'has_special_conditions' && value === false) {
                next.special_conditions = '';
            }
            return next;
        });
    };

    const heading = page === 'init'
        ? t('Vratky a reklamace')
        : typVraceni === 'vratka' ? t('Vratka') : t('Reklamace');

    return (
        <div className="vr">
            <h2 className="vr-heading">{heading}</h2>

            {page === 'init' && (
                <ChoiceBoxes
                    onPick={(typ) => {
                        setTypVraceni(typ);
                        update('duvod_vraceni', typ === 'reklamace' ? 'reklamace' : '14_dni');
                        setPage('baseInput');
                    }}
                />
            )}

            {page === 'baseInput' && (
                <BaseInput
                    api={api}
                    userInput={userInput}
                    update={update}
                    data={data}
                    emailHints={emailHints}
                    setData={setData}
                    setContact={(contact) => setUserInput((prev) => ({ ...prev, ...contact }))}
                    link={setPage}
                />
            )}

            {page === 'product_selector' && data && (
                <ProductSelector
                    userInput={userInput}
                    update={update}
                    products={data.polozky}
                    typVraceni={typVraceni}
                    link={setPage}
                />
            )}

            {page === 'form' && data && typVraceni === 'vratka' && (
                <VratkaForm userInput={userInput} update={update} data={data} link={setPage} />
            )}

            {page === 'form' && data && typVraceni === 'reklamace' && (
                <ReklamaceForm userInput={userInput} update={update} data={data} link={setPage} />
            )}

            {page === 'contact' && (
                <KontaktniUdaje userInput={userInput} update={update} link={setPage} />
            )}

            {page === 'summary' && data && (
                <Summary
                    api={api}
                    userInput={userInput}
                    data={data}
                    typVraceni={typVraceni}
                    setStitekInfo={setStitekInfo}
                    link={setPage}
                />
            )}

            {page === 'finished' && (
                <Finished typVraceni={typVraceni} stitekInfo={stitekInfo} language={language} />
            )}
        </div>
    );
}

export default function VratkyApp(props: VratkyAppProps) {
    return (
        <I18nProvider url={props.translationsUrl} loading={<div className="vr-loading" />}>
            <App {...props} />
        </I18nProvider>
    );
}
